import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import config from '../../config';
import { captureEvent } from '../../services/analytics';
import styles from './TurnstileWidget.module.scss';

const TURNSTILE_SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

// Backstop for a load that neither resolves nor errors — some content blockers
// (and DNS-level blocking) stall the request, so `error` never fires and the
// container would just stay empty forever. Kept well clear of the normal tail
// for a third-party script on a cold mobile connection: anything shorter shows
// a scary message to slow-4G users who were about to get a working widget.
const SCRIPT_LOAD_TIMEOUT_MS = 15000;

/**
 * Stable id for the status paragraph so the pages can point the submit button's
 * `aria-describedby` at it. This message gates account creation — a user who
 * cannot perceive it cannot sign up.
 */
export const TURNSTILE_MESSAGE_ID = 'turnstile-status-message';

// The hard-failure copy: the script definitively did not load, so the advice is
// definite too.
const BLOCKED_MESSAGE =
  "The verification service couldn't load. Please disable content blockers and reload, or email info@jobbeast.org.";

// The timeout path is genuinely ambiguous — slow network and blocked script look
// identical from here — so the copy leads with the benign reading.
const TIMEOUT_MESSAGE =
  "Verification is taking longer than usual. If it doesn't appear, disable content blockers and reload, or email info@jobbeast.org.";

declare global {
  interface Window {
    turnstile?: {
      // Returns undefined (it does not throw) when Cloudflare declines to
      // render — an invalid sitekey being the case that matters here.
      render: (
        el: HTMLElement,
        opts: {
          sitekey: string;
          callback: (token: string) => void;
          'expired-callback'?: () => void;
          'error-callback'?: () => void;
          theme?: 'light' | 'dark' | 'auto';
          size?: 'normal' | 'flexible' | 'compact';
          'refresh-expired'?: 'auto' | 'manual' | 'never';
          action?: string;
        },
      ) => string | undefined;
      reset: (widgetId?: string) => void;
      remove?: (widgetId?: string) => void;
    };
  }
}

/**
 * The request field the token goes into. The two auth endpoints disagree, and
 * getting it wrong is a SILENT failure on the login side, so never inline the
 * string — import the constant.
 *
 *   POST /api/v1/users/registrations  JSON              turnstileToken
 *   POST /api/v1/oauth/token          form-urlencoded   turnstile_token
 *
 * Registration's DTO is `extra = "forbid"`, so a wrong name there is a loud 422
 * naming the field. `OAuth2PasswordRequestForm` ignores unknown fields, so a
 * wrong name on login leaves the token empty and yields a 403 with
 * `error_code: "turnstile_verification_failed"` — indistinguishable from a
 * blocked bot, with no Sentry event.
 */
export const TURNSTILE_TOKEN_FIELD_REGISTRATION = 'turnstileToken';
export const TURNSTILE_TOKEN_FIELD_LOGIN = 'turnstile_token';

export interface TurnstileWidgetHandle {
  /** The current token, or '' if unsolved / expired / already reset. */
  getToken: () => string;
  /**
   * Drop the current token and re-run the challenge.
   *
   * Turnstile tokens are SINGLE USE: the backend redeems the token on the
   * first submit, so a retry that reuses it always fails. Both auth endpoints
   * verify BEFORE they do anything else — before the password check on
   * /oauth/token, before the user write on /users/registrations — so the token
   * is spent by the time almost any failure comes back, including a 401 for a
   * mistyped password and a 422 for a bad phone. Call this after any failed
   * submit that got an HTTP response at all; `isTurnstileTokenSpent` in
   * utils/errorUtils owns that judgement and the reasoning behind it (notably:
   * do NOT reset when the request got no response at all, which would strand an
   * offline user behind a challenge they cannot complete).
   */
  reset: () => void;
}

interface TurnstileWidgetProps {
  /** Applied to the outer wrapper so pages own the widget's layout. */
  className?: string;
  /**
   * Cloudflare's per-widget label ('signup' / 'login'). Without it the two
   * auth flows are indistinguishable in Turnstile analytics — which is the one
   * dashboard worth watching during the enforcement rollout.
   */
  action?: string;
  /**
   * Fires with the new token whenever it changes, and with '' whenever it
   * stops being usable (expired, errored, reset). Token availability is state
   * the page has to RENDER on — enabling a submit button, clearing a "please
   * complete the challenge" message — and a ref cannot drive rendering.
   * `getToken()` remains the right way to read it AT submit time.
   */
  onTokenChange?: (token: string) => void;
}

type BlockedReason = 'script_error' | 'load_timeout' | 'render_failed';

/**
 * Cloudflare Turnstile, rendered explicitly.
 *
 * The imperative handle (rather than a callback prop) is deliberate for
 * `reset()`: it is a command that flows parent -> child at an arbitrary later
 * moment — after a submit has come back failed — which a callback prop cannot
 * express without the parent stashing a child-supplied function. `getToken()`
 * rides along on the same handle so reading the token at submit time costs no
 * re-render. `onTokenChange` covers the other direction, where the parent needs
 * to re-render as the token comes and goes.
 */
const TurnstileWidget = forwardRef<TurnstileWidgetHandle, TurnstileWidgetProps>(
  ({ className, action, onTokenChange }, ref) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const widgetIdRef = useRef<string | null>(null);
    const tokenRef = useRef('');
    const [message, setMessage] = useState<string | null>(null);

    // Read through a ref so a parent passing an inline arrow does not tear the
    // widget down and rebuild it on every render.
    const onTokenChangeRef = useRef(onTokenChange);
    useEffect(() => {
      onTokenChangeRef.current = onTokenChange;
    });

    const emitToken = useCallback((token: string) => {
      tokenRef.current = token;
      onTokenChangeRef.current?.(token);
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        getToken: () => tokenRef.current,
        reset: () => {
          emitToken('');
          if (widgetIdRef.current !== null) {
            window.turnstile?.reset(widgetIdRef.current);
          }
        },
      }),
      [emitToken],
    );

    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      let timeoutId: ReturnType<typeof setTimeout> | undefined;
      let script: HTMLScriptElement | null = null;

      const block = (reason: BlockedReason, copy: string) => {
        setMessage(copy);
        // A user whose api.js never loaded never reaches the backend, so the
        // API's rejection metrics cannot see them by construction. This is the
        // only way to learn what share of signups die before the POST.
        captureEvent('turnstile_blocked', { reason, action: action ?? 'unspecified' });
      };

      const renderWidget = () => {
        if (!window.turnstile || widgetIdRef.current !== null) return;
        if (timeoutId !== undefined) clearTimeout(timeoutId);
        const widgetId = window.turnstile.render(container, {
          sitekey: config.turnstileSiteKey,
          // Unset means 'auto', which follows the OS and would put a dark
          // widget on these light-only forms. Nothing in src/ opts into
          // prefers-color-scheme, so pin it.
          theme: 'light',
          // 'flexible' fills the wrapper's width (never below 300px) at the
          // normal 65px height, so the pages can line the widget up with their
          // form fields instead of hosting a fixed 300px card that aligns with
          // nothing.
          size: 'flexible',
          // Tokens expire after ~300s. This is Cloudflare's current default,
          // but leaving it implicit means a form left open for six minutes
          // submits '' if that default ever moves.
          'refresh-expired': 'auto',
          ...(action ? { action } : {}),
          callback: (token: string) => emitToken(token),
          // A token that expired or errored is worthless; clearing it makes
          // the parent's "no token" guard fire instead of the backend's 403.
          'expired-callback': () => emitToken(''),
          'error-callback': () => emitToken(''),
        });
        if (!widgetId) {
          // Cloudflare declined to render — an invalid sitekey is the case
          // that bites, and it RETURNS UNDEFINED rather than throwing. Storing
          // that undefined would read as "already rendered" to the guard
          // above, so nothing would ever retry and the user would get a silent
          // empty box over a submit button that can never work.
          block('render_failed', BLOCKED_MESSAGE);
          return;
        }
        setMessage(null);
        widgetIdRef.current = widgetId;
      };

      const handleScriptError = () => {
        // Drop the dead tag so a later remount retries the load instead of
        // waiting on listeners that will never fire. Unconditionally correct
        // here: `error` means this element is finished.
        script?.remove();
        if (timeoutId !== undefined) clearTimeout(timeoutId);
        block('script_error', BLOCKED_MESSAGE);
      };

      const handleLoadTimeout = () => {
        // api.js may have arrived through a different element (an earlier
        // mount's tag) while ours sat idle — that is a success, not a block.
        if (window.turnstile) {
          renderWidget();
          return;
        }
        // Now known dead or stalled. Removing the tag is what stops it
        // poisoning every later mount: a tag whose `load` and `error` have
        // already fired still matches `querySelector`, and listeners attached
        // to it wait on events that can never come again.
        script?.remove();
        block('load_timeout', TIMEOUT_MESSAGE);
      };

      if (window.turnstile) {
        renderWidget();
      } else {
        // One shared tag for the whole document: mounting the widget on two
        // pages in a row must not inject the script twice.
        const existing = document.querySelector<HTMLScriptElement>(
          `script[src="${TURNSTILE_SCRIPT_SRC}"]`,
        );
        script = existing ?? document.createElement('script');
        script.addEventListener('load', renderWidget);
        script.addEventListener('error', handleScriptError);
        if (!existing) {
          script.src = TURNSTILE_SCRIPT_SRC;
          script.async = true;
          document.head.appendChild(script);
        }
        timeoutId = setTimeout(handleLoadTimeout, SCRIPT_LOAD_TIMEOUT_MS);
      }

      return () => {
        script?.removeEventListener('load', renderWidget);
        script?.removeEventListener('error', handleScriptError);
        if (timeoutId !== undefined) clearTimeout(timeoutId);
        // React drops our container on unmount but Cloudflare keeps its own
        // registry entry, so hand the widget back or every signin/signup
        // navigation leaks one. Clearing the id and the token is then
        // mandatory: the id because it no longer names a live widget, and the
        // token because it belongs to a widget that no longer exists — leaving
        // it set means the next effect run answers getToken() with a token the
        // page would happily post for a widget Cloudflare has already dropped.
        // Emitted rather than assigned so a parent that re-renders on token
        // availability is not left believing it still holds one.
        if (widgetIdRef.current !== null) {
          window.turnstile?.remove?.(widgetIdRef.current);
          widgetIdRef.current = null;
          emitToken('');
        }
      };
    }, [action, emitToken]);

    return (
      <div className={className}>
        <div ref={containerRef} className={styles.widget} />
        {message && (
          <p id={TURNSTILE_MESSAGE_ID} className={styles.statusMessage} role="alert">
            {message}
          </p>
        )}
      </div>
    );
  },
);

TurnstileWidget.displayName = 'TurnstileWidget';

export default TurnstileWidget;
